<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    GoogleOAuthButton,
    Spinner,
    SimpleDivider,
    MfaVerification,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    allowedLoginMethods() {
      return window.chatwootConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.chatwootConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.chatwootConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      // Use a method to get the translated text to avoid dynamic key warning
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      // wait for idle state
      this.requestIdleCallbackPolyfill(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      // Avoid dynamic key warning by handling each case explicitly
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          // Check if MFA is required
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          // Reset URL Params if the authentication is invalid
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      // MFA verification successful, continue with login
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      // User cancelled MFA, reset state
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
  },
};
</script>

<!-- TODO: 自研登录页品牌化 inline-style 暂以 disable 绕开,
     下一步统一迁移到 Tailwind class -->
<!-- eslint-disable vue/no-static-inline-styles -->
<template>
  <main
    class="flex flex-col items-center justify-center w-full min-h-screen px-4"
    style="background-color: #f5f7fa"
  >
    <!-- Logo Section -->
    <section class="mb-6">
      <img
        :src="globalConfig.logo"
        :alt="globalConfig.installationName"
        class="block w-auto mx-auto"
        style="height: 64px"
      />
    </section>

    <!-- Title -->
    <h2 class="mb-8 text-3xl font-medium text-center" style="color: #1f2937">
      {{ replaceInstallationName($t('LOGIN.TITLE')) }}
    </h2>

    <!-- MFA Verification Section -->
    <section v-if="mfaRequired" class="w-full max-w-md">
      <MfaVerification
        :mfa-token="mfaToken"
        @verified="handleMfaVerified"
        @cancel="handleMfaCancel"
      />
    </section>

    <!-- Login Card -->
    <section
      v-else
      class="w-full max-w-md rounded-2xl"
      style="
        background-color: #ffffff;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      "
      :class="{ 'animate-wiggle': loginApi.hasErrored }"
    >
      <div v-if="!email" class="p-8">
        <!-- OAuth and SAML buttons -->
        <div
          v-if="showGoogleOAuth || showSamlLogin"
          class="flex flex-col gap-4 mb-6"
        >
          <GoogleOAuthButton v-if="showGoogleOAuth" />
          <div v-if="showSamlLogin" class="text-center">
            <router-link
              to="/app/login/sso"
              class="inline-flex justify-center w-full px-4 py-3 items-center rounded-lg"
              style="background-color: #f5f7fa; border: 1px solid #e5e7eb"
            >
              <Icon
                icon="i-lucide-lock-keyhole"
                class="size-5"
                style="color: #6b7280"
              />
              <span class="ml-2 text-base font-medium" style="color: #1f2937">
                {{ $t('LOGIN.SAML.LABEL') }}
              </span>
            </router-link>
          </div>
          <SimpleDivider
            :label="$t('COMMON.OR')"
            bg="bg-white"
            class="uppercase"
          />
        </div>

        <!-- Login Form -->
        <form class="space-y-5" @submit.prevent="submitFormLogin">
          <!-- Email Field -->
          <div>
            <label
              for="email_address"
              class="block mb-2 text-sm font-medium"
              style="color: #1f2937"
            >
              {{ $t('LOGIN.EMAIL.LABEL') }}
            </label>
            <input
              id="email_address"
              v-model="credentials.email"
              name="email_address"
              type="text"
              data-testid="email_input"
              :tabindex="1"
              required
              :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
              class="w-full px-4 py-3 text-base rounded-lg outline-none"
              style="
                background-color: #ffffff;
                border: 1px solid #e5e7eb;
                color: #1f2937;
              "
              :style="
                v$.credentials.email.$error ? 'border-color: #ef4444;' : ''
              "
              @input="v$.credentials.email.$touch"
              @focus="$event.target.style.borderColor = '#1f93ff'"
              @blur="
                $event.target.style.borderColor = v$.credentials.email.$error
                  ? '#ef4444'
                  : '#e5e7eb'
              "
            />
          </div>

          <!-- Password Field -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label
                for="password"
                class="text-sm font-medium"
                style="color: #1f2937"
              >
                {{ $t('LOGIN.PASSWORD.LABEL') }}
              </label>
              <router-link
                v-if="!globalConfig.disableUserProfileUpdate"
                to="auth/reset/password"
                class="text-sm"
                style="color: #1f93ff"
                tabindex="4"
              >
                {{ $t('LOGIN.FORGOT_PASSWORD') }}
              </router-link>
            </div>
            <input
              id="password"
              v-model="credentials.password"
              type="password"
              name="password"
              data-testid="password_input"
              required
              :tabindex="2"
              :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
              class="w-full px-4 py-3 text-base rounded-lg outline-none"
              style="
                background-color: #ffffff;
                border: 1px solid #e5e7eb;
                color: #1f2937;
              "
              :style="
                v$.credentials.password.$error ? 'border-color: #ef4444;' : ''
              "
              @input="v$.credentials.password.$touch"
              @focus="$event.target.style.borderColor = '#1f93ff'"
              @blur="
                $event.target.style.borderColor = v$.credentials.password.$error
                  ? '#ef4444'
                  : '#e5e7eb'
              "
            />
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            data-testid="submit_button"
            :tabindex="3"
            :disabled="loginApi.showLoading"
            class="w-full py-3 text-base font-medium text-white rounded-lg transition-all"
            style="background-color: #1f93ff"
            @mouseenter="$event.target.style.backgroundColor = '#1a7fd9'"
            @mouseleave="$event.target.style.backgroundColor = '#1f93ff'"
          >
            <span v-if="!loginApi.showLoading">{{ $t('LOGIN.SUBMIT') }}</span>
            <Spinner v-else color-scheme="primary" size="small" />
          </button>
        </form>
      </div>
      <div v-else class="flex items-center justify-center p-8">
        <Spinner color-scheme="primary" size="" />
      </div>
    </section>

    <!-- Signup Link -->
    <p
      v-if="showSignupLink"
      class="mt-6 text-sm text-center"
      style="color: #6b7280"
    >
      {{ $t('COMMON.OR') }}
      <router-link to="auth/signup" class="lowercase" style="color: #1f93ff">
        {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
      </router-link>
    </p>
  </main>
</template>
